// CastPickAnimator.tsx — Playback toolbar for the cast pick sequence animation.
// Pure presentational component: no RAF logic, no side effects.

import type { PickStep, PickAnimState } from '../types/panelvar';

interface Props {
  steps:      PickStep[];
  animState:  PickAnimState;
  onSetState: (patch: Partial<PickAnimState>) => void;
}

export default function CastPickAnimator({ steps, animState, onSetState }: Props) {
  const { isPlaying, currentStep, speed, direction } = animState;
  const total = steps.length;
  const current = steps[currentStep];

  const btnStyle: React.CSSProperties = {
    background: 'none',
    border: '1px solid #3a4a5a',
    borderRadius: 4,
    color: '#ccc',
    cursor: 'pointer',
    padding: '3px 8px',
    fontSize: 14,
    lineHeight: 1,
  };

  const activeBtn: React.CSSProperties = {
    ...btnStyle,
    borderColor: '#4da6ff',
    color: '#4da6ff',
  };

  return (
    <div style={{
      position: 'absolute',
      bottom: 28,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      background: 'rgba(20,26,38,0.92)',
      border: '1px solid #2a3a4a',
      borderRadius: 8,
      padding: '6px 12px',
      backdropFilter: 'blur(4px)',
      pointerEvents: 'all',
      zIndex: 10,
      whiteSpace: 'nowrap',
    }}>
      {/* Skip to start */}
      <button style={btnStyle} title="Skip to start"
        onClick={() => onSetState({ currentStep: 0, isPlaying: false })}>
        ⏮
      </button>

      {/* Rewind */}
      <button style={direction === -1 && isPlaying ? activeBtn : btnStyle}
        title="Rewind"
        onClick={() => onSetState({ direction: -1, isPlaying: true })}>
        ⏪
      </button>

      {/* Play / Pause */}
      <button style={isPlaying ? activeBtn : btnStyle}
        title={isPlaying ? 'Pause' : 'Play'}
        onClick={() => onSetState({ isPlaying: !isPlaying, direction: direction < 0 && !isPlaying ? 1 : direction })}>
        {isPlaying ? '⏸' : '▶'}
      </button>

      {/* Fast forward */}
      <button style={direction === 1 && isPlaying ? activeBtn : btnStyle}
        title="Fast forward"
        onClick={() => onSetState({ direction: 1, isPlaying: true })}>
        ⏩
      </button>

      {/* Skip to end */}
      <button style={btnStyle} title="Skip to end"
        onClick={() => onSetState({ currentStep: total - 1, isPlaying: false })}>
        ⏭
      </button>

      <div style={{ width: 1, height: 18, background: '#2a3a4a', margin: '0 2px' }} />

      {/* Speed toggle */}
      <button style={btnStyle}
        title="Playback speed"
        onClick={() => onSetState({ speed: speed === 1 ? 2 : speed === 2 ? 4 : 1 })}>
        {speed}×
      </button>

      <div style={{ width: 1, height: 18, background: '#2a3a4a', margin: '0 2px' }} />

      {/* Step counter and description */}
      <span style={{ fontSize: 11, color: '#aaa', minWidth: 160, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {total > 0
          ? `Step ${currentStep + 1} / ${total}${current ? ` — ${current.description}` : ''}`
          : 'No steps'}
      </span>
    </div>
  );
}
